import React from 'react';
import { ArrowRight, CalendarCheck, Check, FileSearch, HelpCircle, Lightbulb, Megaphone, PartyPopper, TrendingUp, Unlock, UserCheck, Clock } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Localized } from '../../types/notice';
import type { AttentionTarget } from '../../utils/requestDashboard';
import type { BriefingBadge, BriefingDraft, BriefingItem } from '../../utils/territorialBriefing';
interface BriefingCardProps {
  item: BriefingItem;
  sentKinds: string[];
  onPrepare: (draft: BriefingDraft) => void;
  onOpen: (target: AttentionTarget) => void;
  wide?: boolean;
}
const BADGES: Record<BriefingBadge, {
  label: Localized;
  className: string;
  icon: typeof TrendingUp;
}> = {
  rising: {
    label: {
      fr: 'En hausse',
      en: 'Rising'
    },
    className: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: TrendingUp
  },
  unblock: {
    label: {
      fr: 'À débloquer',
      en: 'To unblock'
    },
    className: 'bg-red-50 text-red-700 border-red-200',
    icon: Unlock
  },
  followup: {
    label: {
      fr: 'Suivi attendu',
      en: 'Follow-up expected'
    },
    className: 'bg-indigo-50 text-[#4F46E5] border-indigo-200',
    icon: Clock
  },
  share: {
    label: {
      fr: 'À partager',
      en: 'To share'
    },
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: PartyPopper
  }
};
function Label({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1 flex items-center gap-1" data-id="element-5177" data-mp="1s7az1qwhipwb">
      {children}
    </div>;
}

/** One situation: facts, why it matters, documented explanation, suggestion, actions. */
export function BriefingCard({
  item,
  sentKinds,
  onPrepare,
  onOpen,
  wide
}: BriefingCardProps) {
  const {
    t,
    lang
  } = useLanguage();
  const badge = BADGES[item.badge];
  const BadgeIcon = badge.icon;
  const isShare = item.badge === 'share';
  const exp = item.explanation;
  return <article className={`border rounded-xl bg-white flex flex-col ${isShare ? 'border-emerald-200' : 'border-slate-200'} ${wide ? 'lg:flex-row' : ''}`} data-id="element-5178" data-mp="1xq2yyf94u78">
      <div className={`p-5 flex flex-col gap-4 flex-1 min-w-0 ${wide ? 'lg:border-r lg:border-slate-100' : ''}`} data-id="element-5179" data-mp="2u24guo4skniv">
        <div data-id="element-5180" data-mp="2g58xo2uttfp3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${badge.className}`} data-id="element-5181" data-mp="2l28ynzctjs9c">
            <BadgeIcon className="w-3 h-3" data-id="element-5182" data-mp="26brxmh43j4cz" />
            {badge.label[lang]}
          </span>
          <h3 className="mt-2 text-[15px] font-bold text-[#0F172A] font-heading leading-snug" data-id="element-5183" data-mp="2b5mhbkjpbjc2">
            {item.title[lang]}
          </h3>
          <p className="mt-1.5 text-sm text-slate-600 leading-relaxed" data-id="element-5184" data-mp="22brbsbeo3qvw">{item.summary[lang]}</p>
        </div>

        <div data-id="element-5185" data-mp="1nequl1u3rebo">
          <Label data-id="element-5186" data-mp="1ne79xdd2ibty">{t('Pourquoi cela mérite votre attention', 'Why it deserves your attention')}</Label>
          <p className="text-sm text-slate-600 leading-relaxed" data-id="element-5187" data-mp="1sdtp3scgi25e">{item.why[lang]}</p>
        </div>

        {exp && <div className={`rounded-lg px-3 py-2.5 text-sm border ${exp.status === 'unknown' ? 'bg-slate-50 border-dashed border-slate-300' : 'bg-slate-50 border-slate-200'}`} data-id="element-5188" data-mp="18sjo7nr7uz8z">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-0.5" data-id="element-5189" data-mp="1dr4ofn6gxixf">
              {exp.status === 'unknown' ? <HelpCircle className="w-3.5 h-3.5 text-slate-400" data-id="element-5190" data-mp="1rp320qcytbbv" /> : <FileSearch className="w-3.5 h-3.5 text-slate-500" data-id="element-5191" data-mp="1no9e3wzivwiz" />}
              {exp.status === 'documented' ? t('Explication documentée', 'Documented explanation') : exp.status === 'partial' ? t('Explication partielle', 'Partial explanation') : t('Cause inconnue', 'Unknown cause')}
            </div>
            <p className="text-slate-600 leading-relaxed" data-id="element-5192" data-mp="26glewfaj2pr0">{exp.text[lang]}</p>
            {exp.source && <p className="text-xs text-slate-400 mt-1" data-id="element-5193" data-mp="3tn2vbffqpcb">{exp.source[lang]}</p>}
          </div>}

        {item.engagement && <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2.5" data-id="element-5194" data-mp="8q55o5r7gdm0">
            <div className="flex items-center justify-between gap-2" data-id="element-5195" data-mp="dj6mijaz2n88">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-[#4F46E5]" data-id="element-5196" data-mp="ii15bl8xwbsc">
                <CalendarCheck className="w-3.5 h-3.5" data-id="element-5197" data-mp="3h06qb7xucbq6" />
                {t('Engagement à suivre', 'Commitment to track')}
              </div>
              <span className="text-xs font-semibold text-[#0F172A]" data-id="element-5198" data-mp="3lyqcn9gcjpj2">
                {t('Prévu le', 'Due')} {item.engagement.dueDate[lang]}
              </span>
            </div>
            <div className="text-sm text-[#0F172A] mt-1" data-id="element-5199" data-mp="3vxfk08am9rl2">
              {item.engagement.label[lang]}
              <span className="text-slate-500" data-id="element-5200" data-mp="166u4dqkuyzr6">
                {' '}
                · {item.engagement.owner[lang]}, {item.engagement.confirmedOn[lang]}
              </span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden" role="progressbar" aria-valuenow={item.engagement.progress} aria-valuemin={0} aria-valuemax={100} aria-label={t('Avancement', 'Progress')} data-id="element-5201" data-mp="2vn66m43s4p4c">
              <div className="h-full bg-[#4F46E5] rounded-full" style={{
            width: `${item.engagement.progress}%`
          }} data-id="element-5202" data-mp="2gzbb6fye06nk" />
            </div>
            <div className="mt-1 flex justify-between text-xs text-slate-500" data-id="element-5203" data-mp="27ywq9hpc4tkl">
              <span data-id="element-5204" data-mp="280zp8r8gtx1g">{item.engagement.progressLabel[lang]}</span>
              <span className="font-medium" data-id="element-5205" data-mp="230lev2je6b09">{item.engagement.progress} %</span>
            </div>
          </div>}
      </div>

      <div className={`p-5 flex flex-col gap-3 border-t ${isShare ? 'border-emerald-100 bg-emerald-50/40' : 'border-slate-100 bg-slate-50/60'} ${wide ? 'lg:border-t-0 lg:w-80 shrink-0' : 'mt-auto'} rounded-b-xl ${wide ? 'lg:rounded-bl-none lg:rounded-r-xl' : ''}`} data-id="element-5206" data-mp="1y2tpaf67z0z8">
        <div data-id="element-5207" data-mp="1qnhpafuhofgv">
          <Label data-id="element-5208" data-mp="1vgarox2hjbps">
            <Lightbulb className="w-3 h-3" data-id="element-5209" data-mp="1vgsygnmrx8by" />
            {t('Prochaine étape suggérée', 'Suggested next step')}
          </Label>
          <p className="text-sm text-[#0F172A] leading-relaxed" data-id="element-5210" data-mp="1gsppr6hcjijg">{item.nextStep[lang]}</p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-5211" data-mp="1lr9f7gv5fcgk">
          {item.drafts.map((d, i) => {
          const sent = sentKinds.includes(`${item.id}:${d.kind}`);
          const Icon = sent ? Check : d.kind === 'followup' ? UserCheck : Megaphone;
          const primary = i === 0;
          return <button key={d.kind} onClick={() => !sent && onPrepare(d)} disabled={sent} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${sent ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : primary ? 'bg-[#4F46E5] hover:bg-[#4338CA] text-white' : 'text-slate-700 border border-slate-200 bg-white hover:bg-slate-50'}`} data-id="element-5212" data-mp="1zh35m3may4se" data-mpi={i}>
                <Icon className="w-4 h-4" data-id="element-5213" data-mp="3g8ykueze7wxw" />
                {sent ? d.kind === 'followup' ? t('Demande envoyée', 'Request sent') : t('Transmise aux Communications', 'Sent to Communications') : d.kind === 'followup' ? t('Préparer une demande de suivi', 'Prepare a follow-up request') : t('Préparer une information citoyenne', 'Prepare citizen information')}
              </button>;
        })}
        </div>
        <button onClick={() => onOpen(item.link.target)} className="self-start inline-flex items-center gap-1 text-sm font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-5214" data-mp="2sikpoflihhz3">
          {item.link.label[lang]}
          <ArrowRight className="w-3.5 h-3.5" data-id="element-5215" data-mp="2potp1uzqs2pu" />
        </button>
      </div>
    </article>;
}